import yaml from 'yaml'
import json5 from 'json5'
import type { ZodIssue, ZodSchema } from 'zod'

/**
 * Finds the document type by checking for a property that is in the yaml format with no quotes
 * Handles documents with uncertain initial formatting
 */
export function findDocType(val: string) {
  // Get an arbitrary slice of the initial string to ensure we have enough content to check for a property
  const initial = val.slice(0, 400)
  const yamlReg = /([a-zA-Z0-9]+:)/
  return initial.match(yamlReg) ? 'yaml' : 'json'
}

/**
 * Parsed a string into a typed object
 * Will handle valid yaml, json, and json5 documents
 * Will return an error if the document is not valid
 *
 * Optionally provide a zod schema to validate the document
 */
export function parseAnything<T = unknown>(
  val: string,
  schema?: ZodSchema<T, any, any>,
):
  | { success: true; data: T }
  | {
      success: false
      data: null
      error: string
      issues?: ZodIssue[]
    } {
  const docType = findDocType(val)

  try {
    const obj = docType === 'yaml' ? yaml.parse(val) : json5.parse(val)

    if (schema) {
      const result = schema.safeParse(obj)
      if (result.success) {
        return {
          success: true,
          data: result.data,
        }
      }
      return {
        success: false,
        data: null,
        error: 'Document does not match the schema',
        issues: result.error.issues,
      }
    }
    return {
      success: true,
      data: obj,
    }
  } catch {
    return {
      success: false,
      error: `Could not parse a valid ${docType} document`,
      data: null,
    }
  }
}
